import { useQueryClient } from "@tanstack/react-query";
import { lazy, Suspense, useEffect, type ReactNode } from "react";
import { Link, Navigate, NavLink, Route, Routes, useNavigate } from "react-router";
import { logout } from "./api/client";
import { useHasModules, useLoggedIn, useMe } from "./api/hooks";
import { useI18n } from "./i18n";
import { getPrefs, setPrefs, usePrefs, type Prefs } from "./lib/prefs";
import { Landing } from "./pages/Landing";
import { Forgot, Login, Register, Reset, Verify } from "./pages/Auth";
import { TrainPage } from "./pages/Train";

// Pages away from the training path load on demand, so the first exercise is
// interactive quickly (§14.1).
const Dashboard = lazy(() => import("./pages/Dashboard").then((m) => ({ default: m.Dashboard })));
const Modules = lazy(() => import("./pages/Modules").then((m) => ({ default: m.Modules })));
const ModulePage = lazy(() => import("./pages/Modules").then((m) => ({ default: m.ModulePage })));
const Favorites = lazy(() => import("./pages/Favorites").then((m) => ({ default: m.Favorites })));
const History = lazy(() => import("./pages/History").then((m) => ({ default: m.History })));
const Replay = lazy(() => import("./pages/History").then((m) => ({ default: m.Replay })));
const Leaderboard = lazy(() => import("./pages/Leaderboard").then((m) => ({ default: m.Leaderboard })));
const Profile = lazy(() => import("./pages/Profile").then((m) => ({ default: m.Profile })));
const Admin = lazy(() => import("./pages/admin/Admin").then((m) => ({ default: m.Admin })));

function RequireLogin({ children }: { children: ReactNode }) {
  return useLoggedIn() ? <>{children}</> : <Navigate to="/login" replace />;
}

function Topbar() {
  const { t, lang, setLang } = useI18n();
  const loggedIn = useLoggedIn();
  const me = useMe();
  const hasModules = useHasModules();
  const prefs = usePrefs();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const nextTheme: Prefs["theme"] =
    prefs.theme === "dark" || (prefs.theme === "system" && window.matchMedia?.("(prefers-color-scheme: dark)").matches)
      ? "light"
      : "dark";
  return (
    <header className="topbar">
      <Link to="/" className="brand">
        <svg width="22" height="22" viewBox="0 0 22 22" aria-hidden>
          <rect width="22" height="22" rx="5" fill="var(--accent)" />
          <circle cx="11" cy="11" r="6" fill="var(--bg)" />
          <circle cx="11" cy="11" r="3.4" fill="none" stroke="var(--accent)" strokeWidth="1.4" />
        </svg>
        <span className="word">{t("app.name")}</span>
      </Link>
      <nav className="nav row" style={{ gap: 2 }}>
        <NavLink to="/train">{t("nav.train")}</NavLink>
        {hasModules && <NavLink to="/modules">{t("nav.modules")}</NavLink>}
        <NavLink to="/leaderboard">{t("nav.leaderboard")}</NavLink>
        {loggedIn && <NavLink to="/favorites" title={t("nav.favorites")}>☆</NavLink>}
        {loggedIn && <NavLink to="/history">{t("nav.history")}</NavLink>}
        {me.data?.role === "admin" && <NavLink to="/admin">{t("nav.admin")}</NavLink>}
      </nav>
      <span className="spacer" />
      <button className="ghost hide-mobile" title={t("nav.theme")} onClick={() => setPrefs({ theme: nextTheme })} aria-label={t("nav.theme")}>
        ◐
      </button>
      <button className="ghost small hide-mobile" onClick={() => setLang(lang === "en" ? "nl" : "en")} aria-label="language">
        {lang === "en" ? "NL" : "EN"}
      </button>
      {loggedIn ? (
        <>
          <NavLink to="/profile" className="nav" title={t("nav.profile")} style={{ display: "flex" }}>
            <span
              style={{
                width: 30, height: 30, borderRadius: "50%", background: "var(--accent)", color: "var(--accent-ink)",
                display: "inline-flex", alignItems: "center", justifyContent: "center", fontWeight: 700,
              }}
            >
              {me.data?.display_name?.[0]?.toUpperCase() ?? "·"}
            </span>
          </NavLink>
          <button
            className="ghost small hide-mobile"
            onClick={async () => {
              await logout();
              qc.clear();
              navigate("/");
            }}
          >
            {t("nav.logout")}
          </button>
        </>
      ) : (
        <>
          <Link className="btn ghost hide-mobile" to="/login">{t("nav.login")}</Link>
          <Link className="btn primary" to="/register">{t("nav.signup")}</Link>
        </>
      )}
    </header>
  );
}

/** Profile settings (sound, board, language…) follow the account once loaded. */
function SyncSettings() {
  const me = useMe();
  const { setLang } = useI18n();
  useEffect(() => {
    const s = me.data?.settings as Partial<Prefs> & { language?: "en" | "nl" } | undefined;
    if (!s) return;
    const patch: Partial<Prefs> = {};
    for (const k of ["sound", "coordinates", "theme", "board_theme", "difficulty", "move_assist", "move_drag", "move_targets", "train_label", "auto_next"] as const) {
      if (s[k] !== undefined && s[k] !== getPrefs()[k]) (patch as Record<string, unknown>)[k] = s[k];
    }
    if (Object.keys(patch).length) setPrefs(patch);
    if (s.language) setLang(s.language);
  }, [me.data, setLang]);
  return null;
}

export function App() {
  const loggedIn = useLoggedIn();
  return (
    <div className="app">
      <Topbar />
      <SyncSettings />
      <Suspense fallback={<div className="page muted">…</div>}>
        <Routes>
          <Route path="/" element={loggedIn ? <Dashboard /> : <Landing />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/verify" element={<Verify />} />
          <Route path="/forgot" element={<Forgot />} />
          <Route path="/reset" element={<Reset />} />
          <Route path="/train" element={loggedIn ? <TrainPage mode="rated" /> : <TrainPage mode="taster" />} />
          <Route path="/train/daily" element={<TrainPage mode="daily" />} />
          <Route path="/train/retry/:exerciseId" element={<RequireLogin><TrainPage mode="rated" /></RequireLogin>} />
          <Route path="/train/mistakes" element={<RequireLogin><TrainPage mode="mistakes" /></RequireLogin>} />
          <Route path="/train/favorites" element={<RequireLogin><TrainPage mode="favorites" /></RequireLogin>} />
          <Route path="/modules" element={<RequireLogin><Modules /></RequireLogin>} />
          <Route path="/modules/:id" element={<RequireLogin><ModulePage /></RequireLogin>} />
          <Route path="/modules/:id/train" element={<RequireLogin><TrainPage mode="module" /></RequireLogin>} />
          <Route path="/favorites" element={<RequireLogin><Favorites /></RequireLogin>} />
          <Route path="/history" element={<RequireLogin><History /></RequireLogin>} />
          <Route path="/history/:attemptId" element={<RequireLogin><Replay /></RequireLogin>} />
          <Route path="/leaderboard" element={<Leaderboard />} />
          <Route path="/profile" element={<RequireLogin><Profile /></RequireLogin>} />
          <Route path="/admin/*" element={<RequireLogin><Admin /></RequireLogin>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </div>
  );
}
